/**
 * Reliability Quick Widget - Phase 14
 * 
 * Lightweight widget for dashboard showing reliability status at a glance.
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, AlertTriangle, CheckCircle, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { getMerchantReliabilitySummary } from '@/lib/reliability/reliabilitySelectors';
import { RELIABILITY_STATUS_CONFIG } from '@/lib/reliability/reliabilityModel';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

export function ReliabilityQuickWidget({ className = '' }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReliability();
    
    // Refresh every 2 minutes
    const interval = setInterval(loadReliability, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadReliability = async () => {
    try {
      const data = await getMerchantReliabilitySummary({});
      setSummary(data);
      setError(null);
    } catch (err) {
      console.error('[ReliabilityWidget] Failed to load:', err);
      setError('Unable to load reliability data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = () => {
    return RELIABILITY_STATUS_CONFIG[summary?.reliabilityStatus] || RELIABILITY_STATUS_CONFIG.healthy;
  };

  const statusConfig = getStatusConfig();
  
  const colorMap = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  };

  if (loading) {
    return (
      <Card className={className} data-testid="reliability-widget-loading">
        <CardContent className="pt-6 flex items-center justify-center min-h-[120px]">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className} data-testid="reliability-widget-error">
        <CardContent className="pt-6 text-center text-gray-500 min-h-[120px] flex items-center justify-center">
          <p className="text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="reliability-quick-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-blue-500" />
            System Reliability
          </CardTitle>
          <Badge className={colorMap[statusConfig.color]}>
            {statusConfig.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Score */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Health Score</span>
              <span className="font-medium">{summary?.reliabilityScore || 0}/100</span>
            </div>
            <Progress value={summary?.reliabilityScore || 0} className="h-2" />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <p className="text-lg font-bold">{summary?.core?.unresolvedIncidentCount || 0}</p>
              <p className="text-xs text-gray-500">Open</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <p className="text-lg font-bold text-red-600">{summary?.core?.criticalIncidentCount || 0}</p>
              <p className="text-xs text-gray-500">Critical</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
              <p className="text-lg font-bold">{summary?.core?.syncSuccessRate?.toFixed(0) || 100}%</p>
              <p className="text-xs text-gray-500">Sync</p>
            </div>
          </div>

          {/* Link to full report */}
          <a
            href={bo('reliability-reporting')}
            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
            data-testid="reliability-widget-link"
          >
            <span>View Full Report</span>
            <ChevronRight className="h-4 w-4" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

export default ReliabilityQuickWidget;
